import {
  Grid,
  List,
  ListItemText,
  ListItemButton,
  Alert,
  ListItemIcon,
  Box,
  Typography
} from '@mui/material';
import { useQuery } from 'react-query';
import { FC, Fragment } from 'react';
import { PandaDocEditor } from '../../../components/pdf-viewer/PandadocEditor';
import {
  getEditSalesToolsContractTemplateSession,
  getSalesToolsContractsTokens
} from '../../../fetch';
import { useSnackbar } from 'notistack';
import { convertToNumber, copyTextToClipboard } from '../../../helpers';
import {
  ISalesToolsContractTemplateEditSession,
  ISalesToolsContractTemplateTokens
} from '../../../models';
import { Loader } from '../../../components';
import { CopyAll } from '@mui/icons-material';

interface IContractMangementEditor {
  pandaDocTemplateId: string;
  buildDocumentTypeId: string;
}

export const ContractMangementEditor: FC<IContractMangementEditor> = ({
  buildDocumentTypeId,
  pandaDocTemplateId
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { data: replacementFields, isLoading: isLoadingFields } = useQuery<
    ISalesToolsContractTemplateTokens[],
    Error
  >(['getSalesToolsContractsTokens-contract-management', buildDocumentTypeId], () =>
    getSalesToolsContractsTokens(buildDocumentTypeId)
  );

  const { data, isLoading: isLoadingSession } = useQuery<
    ISalesToolsContractTemplateEditSession,
    Error
  >(
    ['getSalesToolsContractsTokens-contract-management', buildDocumentTypeId, pandaDocTemplateId],
    () =>
      getEditSalesToolsContractTemplateSession(
        convertToNumber(buildDocumentTypeId),
        pandaDocTemplateId
      )
  );

  const validReplacementFields = replacementFields?.filter(field => field !== null);

  return (
    <Box position="relative">
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          {isLoadingFields && <Loader type="overlay" position="centered" />}
          <Alert severity="info" sx={{ marginBottom: 2 }}>
            Click on the "Plus" (+) sign in the top right corner. From the Fillable Fields section,
            add checkmarks where needed, and add text fields where filled text should appear. For
            filled text, choose the "Properties" icon. Then, copy and paste the field needed from
            the menu below, to the "Merge Field".
          </Alert>
          {!isLoadingFields && validReplacementFields?.length === 0 ? (
            <Typography variant="body2" textAlign="center" sx={{ mt: 2 }}>
              No replacement fields available
            </Typography>
          ) : (
            <List component="div" dense sx={{ maxHeight: 700, overflowY: 'auto' }}>
              {validReplacementFields?.map((field, index) => (
                <Fragment key={index}>
                  <ListItemButton
                    dense
                    onClick={async () => {
                      try {
                        await copyTextToClipboard(field?.value);
                        enqueueSnackbar('Field copied to clipboard', { variant: 'success' });
                      } catch (error) {
                        enqueueSnackbar('Failed to copy to clipboard, please try again', {
                          variant: 'error'
                        });
                      }
                    }}
                  >
                    <ListItemText primary={field?.description} />
                    <ListItemIcon>
                      <CopyAll />
                    </ListItemIcon>
                  </ListItemButton>
                </Fragment>
              ))}
            </List>
          )}
        </Grid>
        <Grid item xs={12} lg={9}>
          {isLoadingSession && <Loader type="overlay" position="centered" />}
          {!isLoadingSession && (
            <PandaDocEditor
              pandaDocTemplateId={pandaDocTemplateId}
              token={data.key}
              editingSessionId={data.editingSessionId}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
